import {
  isDeckType,
  isSidingType,
  slotsForSurface,
  coverageUnit,
  productsForRoofType,
  templateIsUnbuilt,
  UNBUILT_TEMPLATE_LABEL,
  type Product,
  type RoofTemplate,
  type RoofTemplateItemRow,
} from "@roof-hawk/core";
import { useCallback, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { deleteRow, insertRow, updateRow } from "../../lib/queries";
import { supabase, unwrap } from "../../lib/supabase";
import { ProductSelect } from "../estimate/EstimateCard";

/**
 * Roof templates.
 *
 * The list is links, not an editor: 25 templates with a dozen sections each is
 * far too much to hold on one screen. Click a template and you are editing that
 * template and nothing else.
 */
export function TemplatesTool() {
  const [openId, setOpenId] = useState<string | null>(null);

  const templates = useAsync(
    async () =>
      unwrap<RoofTemplate[]>(
        await supabase
          .from("roof_templates")
          .select("*")
          .order("sort_order")
          .order("name"),
      ),
    [],
  );

  const counts = useAsync(
    async () =>
      unwrap<{ template_id: string }[]>(
        await supabase.from("roof_template_items").select("template_id"),
      ),
    [],
  );

  if (openId) {
    const template = (templates.data ?? []).find((t) => t.id === openId);
    if (!template) return null;
    return (
      <TemplateEditor
        template={template}
        onBack={async () => {
          setOpenId(null);
          await Promise.all([counts.reload(), templates.reload()]);
        }}
      />
    );
  }

  const byTemplate = new Map<string, number>();
  for (const row of counts.data ?? []) {
    byTemplate.set(row.template_id, (byTemplate.get(row.template_id) ?? 0) + 1);
  }

  return (
    <>
      <ErrorNote error={templates.error} />
      <div className="na-hint" style={{ marginBottom: 10 }}>
        {t(
          "Click a template to add the products that go on it. A template with no products generates nothing. Mark one as finished from inside it, and the badge comes off everywhere.",
        )}
      </div>
      <div className="list">
        <div className="lrow head tmplrow">
          <div>{t("Template")}</div>
          <div>{t("Trade")}</div>
          <div>{t("Roof Type")}</div>
          <div>{t("Products")}</div>
        </div>
        {(templates.data ?? []).map((template) => {
          const count = byTemplate.get(template.id) ?? 0;
          return (
            <div
              key={template.id}
              className="lrow tmplrow"
              onClick={() => setOpenId(template.id)}
            >
              <div className="cust">
                {template.name}
                {templateIsUnbuilt(template) && (
                  <span className="cbadge warn" style={{ marginLeft: 8 }}>
                    {t(UNBUILT_TEMPLATE_LABEL)}
                  </span>
                )}
              </div>
              <div className="sub">{template.trade ?? "—"}</div>
              <div className="sub">{template.roof_type ?? "—"}</div>
              <div className={count ? "sub" : "na-hint"}>
                {count ? tn(count, "1 product", "{n} products") : t("empty")}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * A slot's heading in the app's language.
 *
 * Most slots come in pairs, "Eave products" and "Eave products ea", so the
 * part of the roof is translated once and put into either sentence. A label
 * that is not of that shape is looked up whole.
 */
function slotLabel(label: string): string {
  const match = /^(.+) products( ea)?$/.exec(label);
  if (!match?.[1]) return t(label);
  const part = t(match[1]);
  return match[2]
    ? t("{part} products ea", { part })
    : t("{part} products", { part });
}

/**
 * The sum on one template line, written out.
 *
 * "auto" told you nothing. What the line does is either a fixed count — two
 * slant back vents on every roof, big or small — or a rate: one bundle per
 * 33.33 square feet, one stick per 10 feet of eave. Both are shown as the sum
 * they are, and both can be changed here.
 *
 * The two are not the same kind of edit, and that is the point of the
 * confirmation. A fixed count belongs to this template. A rate belongs to the
 * product, so changing it moves every template that uses it and reprices every
 * estimate generated from here on. Nobody should discover that afterwards.
 */
function RuleBox({
  row,
  product,
  unit,
  onSaveQty,
  onSaveCoverage,
}: {
  row: RoofTemplateItemRow;
  product: Product | undefined;
  unit: "SQ" | "SF" | "LF" | "EA";
  onSaveQty: (qty: number | null) => Promise<void>;
  onSaveCoverage: (coverage: number) => Promise<void>;
}) {
  const fixed = row.qty != null;
  const coverage = product?.coverage == null ? null : Number(product.coverage);
  const stored = fixed
    ? String(row.qty)
    : coverage && coverage > 0
      ? String(coverage)
      : "";

  const [draft, setDraft] = useState(stored);
  // The row can be replaced under us by a reload; follow it when it is.
  const [seen, setSeen] = useState(stored);
  if (seen !== stored) {
    setSeen(stored);
    setDraft(stored);
  }

  const name = product
    ? [product.category, product.name].join(" | ")
    : t("this");
  const per = product?.unit ?? t("unit");

  async function commit() {
    const next = Number(draft.trim());
    if (!(Number.isFinite(next) && next > 0)) return setDraft(stored);
    if (String(next) === stored) return;

    const question = fixed
      ? t("Put {n} of {name} on every job from this template?", {
          n: next,
          name,
        })
      : // Two keys, because a key with a line break in it cannot be checked.
        t("One {per} of {name} covers {n} {unit}?", {
          per,
          name,
          n: next,
          unit,
        }) +
        "\n\n" +
        t(
          "This is the product's own figure, so it changes every template that uses it and every estimate generated from now on. Estimates already written are not touched.",
        );

    if (!window.confirm(question)) return setDraft(stored);
    await (fixed ? onSaveQty(next) : onSaveCoverage(next));
  }

  return (
    <span className="tmplrule">
      {!fixed && (
        <span className="tmplrule-lead">{t("1 {per} per", { per })}</span>
      )}
      <input
        className="tmplqty"
        inputMode="decimal"
        placeholder={stored === "" ? "—" : ""}
        title={
          fixed
            ? t("A set count, whatever the roof measures.")
            : t("How much one {per} covers.", { per })
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") setDraft(stored);
        }}
      />
      <span className="tmplrule-unit">{fixed ? t("per job") : unit}</span>
    </span>
  );
}

/**
 * One template.
 *
 * Products are grouped by the part of the roof they cover, because that is what
 * decides the quantity when a job is generated: a product in "Eave products" is
 * ordered off eave feet, one in "Ridge products" off the ridge. The "ea" lists
 * are one per job regardless of how big the roof is.
 */
function TemplateEditor({
  template,
  onBack,
}: {
  template: RoofTemplate;
  onBack: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  // Held locally so the tick responds at once; the list re-reads on the way
  // back out.
  const [builtOut, setBuiltOut] = useState(!templateIsUnbuilt(template));

  const products = useAsync(
    async () =>
      unwrap<Product[]>(
        await supabase
          .from("products")
          .select("*")
          .eq("active", true)
          .order("category")
          .order("name"),
      ),
    [],
  );

  const items = useAsync(
    async () =>
      unwrap<RoofTemplateItemRow[]>(
        await supabase
          .from("roof_template_items")
          .select("*")
          .eq("template_id", template.id)
          .order("sort_order"),
      ),
    [template.id],
  );

  const guard = useCallback(
    async (action: () => Promise<void>) => {
      try {
        await action();
        await items.reload();
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [items],
  );

  const rows = items.data ?? [];
  /*
   * The catalogue this template may draw on.
   *
   * A metal template is not offered the GAF Master Flow pipe boots (Jonathon,
   * 2026-08-27) — they are a shingle product and seat on nothing on a ribbed
   * panel. Filtered rather than badged: a dropdown of four hundred products is
   * where the wrong one gets picked.
   */
  const catalogue = productsForRoofType(
    products.data ?? [],
    template.roof_type,
  );
  const nameFor = (id: string) => {
    const p = catalogue.find((x) => x.id === id);
    return p
      ? [p.category, p.name].filter(Boolean).join(" | ")
      : t("(removed)");
  };

  return (
    <>
      <div className="tmplhead">
        <span className="clink" onClick={() => void onBack()}>
          {t("← All templates")}
        </span>
        <h3>{template.name}</h3>
        <span className="na-hint">
          {template.trade ?? t("no trade")}
          {template.roof_type ? ` · ${template.roof_type}` : ""}
          {rows.length === 0
            ? t(" · empty")
            : ` · ${tn(rows.length, "1 product", "{n} products")}`}
        </span>
      </div>

      {/* The owner's own switch. Deliberately not derived from `rows.length` —
          a template halfway through being built would flip itself to finished
          at whatever count was chosen, which is exactly when an estimate must
          still warn. See templateReadiness.ts. */}
      <label
        className="na-hint"
        style={{ display: "block", margin: "0 0 10px" }}
      >
        <input
          type="checkbox"
          checked={builtOut}
          onChange={(e) => {
            const next = e.target.checked;
            setBuiltOut(next);
            void guard(async () => {
              await updateRow("roof_templates", template.id, {
                built_out: next,
              });
            });
          }}
          style={{ marginRight: 6 }}
        />
        {t(
          "Finished — safe to estimate from. Leave this off while you are still adding products, and the app shows it as {label}.",
          { label: t(UNBUILT_TEMPLATE_LABEL).toLowerCase() },
        )}
      </label>

      <ErrorNote error={error} />

      {/* A siding template is built from the wall's sections and a roof
          template from the roof's; showing both would offer a rep forty
          boxes, half of which can never produce a quantity here. */}
      {slotsForSurface(
        isDeckType(template.roof_type)
          ? "deck"
          : isSidingType(template.roof_type)
            ? "wall"
            : "roof",
      ).map((slot) => {
        const inSlot = rows.filter((r) => r.slot === slot.key);
        return (
          <div className="tmplslot" key={slot.key}>
            <div className="tmplslot-head">
              <b>{slotLabel(slot.label)}</b>
              <span className="na-hint">
                {slot.measurement === null
                  ? t("a set count per job")
                  : t("quantity from {measurement}", {
                      measurement: t(slot.measurement.replace(/_/g, " ")),
                    })}
              </span>
            </div>

            {inSlot.map((row) => {
              const product = catalogue.find((p) => p.id === row.product_id);
              return (
                <div className="tmplitem" key={row.id}>
                  <span>{nameFor(row.product_id)}</span>
                  <RuleBox
                    row={row}
                    product={product}
                    unit={coverageUnit(slot.measurement, product?.measurement)}
                    onSaveQty={(qty) =>
                      guard(async () => {
                        await updateRow("roof_template_items", row.id, {
                          qty,
                        });
                      })
                    }
                    onSaveCoverage={(coverage) =>
                      guard(async () => {
                        if (!product) return;
                        await updateRow("products", product.id, { coverage });
                        await products.reload();
                      })
                    }
                  />
                  <button
                    className="xbtn"
                    title={t("Remove")}
                    onClick={() =>
                      void guard(() => deleteRow("roof_template_items", row.id))
                    }
                  >
                    ✕
                  </button>
                </div>
              );
            })}

            <div className="tmpladd">
              <ProductSelect
                products={catalogue}
                value={null}
                width={280}
                onChange={(product) => {
                  if (!product) return;
                  if (inSlot.some((r) => r.product_id === product.id)) return;
                  void guard(async () => {
                    await insertRow("roof_template_items", {
                      template_id: template.id,
                      slot: slot.key,
                      product_id: product.id,
                      sort_order: (inSlot.length + 1) * 10,
                    });
                  });
                }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}
